import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import TopModalSection from '../common/TopModalSection';

import ShopifySubscriptionDiscountForm, {
	ShopifySubscriptionDiscountOptions,
} from '@/components/forms/shopify-subscription-discount/ShopifySubscriptionDiscountForm';
import Loader from '@/components/ui/loader/Loader';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICreateShopifySubscriptionDiscountPayload } from '@/interfaces/api/shopify-subscription-discount/ICreateShopifySubscriptionDiscountPayload';
import { IShopifySubscriptionDiscount } from '@/interfaces/api/shopify-subscription-discount/IShopifySubscriptionDiscount';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const ViewUpdateCreateShopifySubscriptionDiscountModal = ({
	closeAfterSubmit,
	defaultClose,
	shopifySubscriptionDiscountOption,
	id,
}: {
	closeAfterSubmit: () => void;
	defaultClose: () => void;
	shopifySubscriptionDiscountOption: ShopifySubscriptionDiscountOptions;
	id?: string;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const {
		data: editShopifySubscriptionDiscount,
		isLoading: isFetching,
		refetch,
	} = useQuery<IShopifySubscriptionDiscount>({
		queryKey: [ApiQueryKeys.getShopifySubscriptionDiscountById, id],
		queryFn: () => haggleApiService.getShopifySubscriptionDiscountById(id!),
		enabled:
			id !== undefined &&
			typeof id === 'string' &&
			shopifySubscriptionDiscountOption !==
				ShopifySubscriptionDiscountOptions.CREATE,
		refetchOnWindowFocus: true,
	});

	const handleCreateSubmit = async (
		values: ICreateShopifySubscriptionDiscountPayload,
	) => {
		setIsLoading(true);
		try {
			await haggleApiService.createShopifySubscriptionDiscount(values);

			notificationService.success(
				'Shopify Subscription Discount created successfully',
			);
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to create a Shopify Subscription Discount: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
			closeAfterSubmit();
		}
	};

	const handleUpdateSubmit = async (
		values: ICreateShopifySubscriptionDiscountPayload,
	) => {
		if (!id) return;

		setIsLoading(true);
		try {
			await haggleApiService.updateShopifySubscriptionDiscount(id, values);

			notificationService.success(
				'Shopify Subscription Discount updated successfully',
			);

			refetch();
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to update a Shopify Subscription Discount: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
			closeAfterSubmit();
		}
	};

	// Convert API percentage (0-1) to form percentage (0-100)
	const convertApiToFormPercentage = (apiPercentage: number): number => {
		return apiPercentage * 100;
	};

	const handleInitialValues = (
		editShopifySubscriptionDiscount: IShopifySubscriptionDiscount | undefined,
	): ICreateShopifySubscriptionDiscountPayload | undefined => {
		if (editShopifySubscriptionDiscount) {
			return {
				email: editShopifySubscriptionDiscount.email,
				planName: editShopifySubscriptionDiscount.planName,
				percentage: convertApiToFormPercentage(
					editShopifySubscriptionDiscount.percentage,
				),
				durationMonths: editShopifySubscriptionDiscount.durationMonths,
			};
		}

		return undefined;
	};

	if (isFetching)
		return (
			<Loader
				variant="blink"
				className="flex items-center justify-center h-full w-full"
			/>
		);

	return (
		<div
			className="flex flex-col justify-center items-center px-4"
			data-test="view-create-shopify-subscription-discount-modal"
		>
			<TopModalSection
				title={
					shopifySubscriptionDiscountOption ===
					ShopifySubscriptionDiscountOptions.UPDATE
						? 'Update Shopify Subscription Discount'
						: shopifySubscriptionDiscountOption ===
						  ShopifySubscriptionDiscountOptions.CREATE
						? 'Create new Shopify Subscription Discount'
						: 'View Shopify Subscription Discount'
				}
				close={defaultClose}
			/>

			<ShopifySubscriptionDiscountForm
				handleSubmit={
					editShopifySubscriptionDiscount &&
					shopifySubscriptionDiscountOption ===
						ShopifySubscriptionDiscountOptions.UPDATE
						? handleUpdateSubmit
						: handleCreateSubmit
				}
				loading={isLoading || isFetching}
				initialValues={handleInitialValues(editShopifySubscriptionDiscount)}
				shopifySubscriptionDiscountOption={
					editShopifySubscriptionDiscount &&
					shopifySubscriptionDiscountOption ===
						ShopifySubscriptionDiscountOptions.UPDATE
						? ShopifySubscriptionDiscountOptions.UPDATE
						: shopifySubscriptionDiscountOption ===
						  ShopifySubscriptionDiscountOptions.CREATE
						? ShopifySubscriptionDiscountOptions.CREATE
						: ShopifySubscriptionDiscountOptions.VIEW
				}
			/>
		</div>
	);
};

export default ViewUpdateCreateShopifySubscriptionDiscountModal;
